import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Landmark, Paperclip, Users, ArrowRight, Pencil, Eye, Copy, Loader2, Building2 } from "lucide-react";

import { SiteHeader, PageFooter } from "@/components/brand/Brand";
import { StatusBadge } from "@/components/company/StatusBadge";
import { CompanyLogo } from "@/components/company/CompanyLogo";
import { ProfilePreview } from "@/components/company/ProfileView";
import { appCompany } from "@/lib/demo/companies";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { FullLoader } from "@/components/auth/RequireRole";
import { useI18n } from "@/lib/i18n/i18n";
import { BANK } from "@/lib/demo/applications";

const STEPS = ["Solicitud enviada", "Pago por transferencia", "Verificación del pago", "Aprobación UPV"];
const STEP_OF = { pending_payment: 1, payment_review: 2, paid: 3, approved: 4, rejected: 3 };
const MESSAGES = {
  payment_review: "Estamos verificando tu pago. Te avisaremos por email.",
  paid: "Pago confirmado. Tu solicitud está pendiente de aprobación por la UPV.",
  approved: "¡Empresa aprobada! Ya apareces en el directorio de empresas participantes.",
  rejected: "La solicitud ha sido rechazada. Contacta con la organización del Foro.",
};

const ProfileCard = ({ app }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const c = appCompany(app);
  return (
    <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white" data-testid="company-profile-card">
      <div className="relative h-28">
        <img src={c.image} alt={c.name} className="h-full w-full object-cover" data-testid="company-profile-card-cover" />
        <div className="absolute bottom-0 left-5 translate-y-1/2 rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-md"><CompanyLogo id={app.id} logo={c.logo} size="text-lg" /></div>
      </div>
      <div className="p-6 pt-9">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{t("Perfil público")}</p>
        <p className="mt-2 text-sm text-slate-600" data-testid="company-profile-card-info">{t("{n} oportunidades", { n: c.jobs.length })} · {t(app.status === "approved" ? "Visible en el directorio" : "Visible tras la aprobación de la UPV")}</p>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <Link to={`/empresas/inscripcion/${app.id}/editar`} className="flex h-11 items-center justify-center gap-2 rounded-xl bg-upv text-sm font-bold text-white transition-colors hover:bg-upv-dark" data-testid="company-profile-edit-button"><Pencil className="h-4 w-4" /> {t("Editar perfil")}</Link>
          <button onClick={() => setOpen(true)} className="flex h-11 items-center justify-center gap-2 rounded-xl border-2 border-slate-900 text-sm font-bold transition-colors hover:bg-slate-900 hover:text-white" data-testid="company-profile-preview-button"><Eye className="h-4 w-4" /> {t("Vista previa")}</button>
        </div>
      </div>
      <ProfilePreview open={open} onOpenChange={setOpen} data={app} />
    </div>
  );
};

const UsersCard = ({ app }) => {
  const { t } = useI18n();
  const list = app.accounts || app.users;
  const copy = (u) => { navigator.clipboard?.writeText(`${window.location.origin}/activar/${u.activationToken}`); toast.success(t("Enlace de activación copiado")); };
  return (
    <div className="rounded-[28px] border border-slate-200 bg-white p-6" data-testid="company-users-card">
      <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-400"><Users className="h-3.5 w-3.5" /> {t("Gestión de usuarios")}</p>
      <ul className="mt-4 space-y-2">
        {list.map((u, i) => (
          <li key={u.id || i} className="flex items-center justify-between gap-2 rounded-xl bg-slate-50 p-3" data-testid={`company-user-${i}`}>
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-wider text-upv">{t((u.companyRole || u.role) === "admin" ? "Administrador" : "Reclutador")}{u.pendingActivation ? ` · ${t("pendiente de activar")}` : ""}</p>
              <p className="mt-1 truncate text-sm"><span className="font-semibold">{u.name || "—"}</span> <span className="text-slate-500">{u.email}</span></p>
            </div>
            {u.activationToken && <button onClick={() => copy(u)} title={t("Copiar enlace de activación")} className="shrink-0 rounded-lg p-2 text-slate-500 hover:bg-white" data-testid={`company-user-copy-${i}`}><Copy className="h-4 w-4" /></button>}
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-slate-400">{t(app.accounts ? "Gestiona los usuarios desde el portal de empresa (Cuenta)." : "Las cuentas de los reclutadores se crean al aprobarse la solicitud.")}</p>
    </div>
  );
};

const TransferCard = ({ app, onChange }) => {
  const { t } = useI18n();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const rows = [["Beneficiario", BANK.beneficiary], ["IBAN", t(BANK.iban)], ["Importe", t(BANK.amount)], ["Concepto", app.ref]];
  const confirm = async () => {
    if (!file) { toast.error(t("Adjunta el justificante de la transferencia")); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error(t("El justificante supera 5 MB")); return; }
    const fd = new FormData();
    fd.append("file", file, file.name);
    setBusy(true);
    try { const { data } = await api.post("/applications/mine/receipt", fd); onChange(data); toast.success(t("Justificante enviado")); } catch (e) { toast.error(t(apiError(e).message)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-[28px] border border-slate-200 bg-white p-6" data-testid="company-transfer-card">
      <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-upv"><Landmark className="h-3.5 w-3.5" /> {t("Datos para la transferencia")}</p>
      <dl className="mt-4 divide-y divide-slate-100 text-sm">
        {rows.map(([k, v]) => <div key={k} className="flex justify-between gap-4 py-3"><dt className="text-slate-500">{t(k)}</dt><dd className="text-right font-mono font-semibold" data-testid={`company-transfer-${k.toLowerCase()}`}>{v}</dd></div>)}
      </dl>
      <label className="mt-4 flex cursor-pointer items-center gap-3 rounded-xl border-2 border-dashed border-slate-300 p-4 text-sm font-semibold text-slate-600 transition-colors hover:border-upv" data-testid="company-receipt-dropzone">
        <Paperclip className="h-4 w-4 text-upv" /> {file?.name || t("Adjuntar justificante (PDF, PNG o JPG)")}
        <input type="file" accept="application/pdf,image/png,image/jpeg" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="company-receipt-input" />
      </label>
      <button onClick={confirm} disabled={busy} className="mt-4 flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-upv font-display font-bold tracking-wider text-white transition-colors hover:bg-upv-dark disabled:opacity-70" data-testid="company-transfer-confirm-button">{busy && <Loader2 className="h-5 w-5 animate-spin" />}{t("HE REALIZADO LA TRANSFERENCIA")}</button>
      <p className="mt-3 text-xs text-slate-400">{t("El pago se realiza por transferencia fuera de la plataforma. La UPV verifica el justificante.")}</p>
    </div>
  );
};

export default function EmpresaSolicitud() {
  const { appId } = useParams();
  const { t } = useI18n();
  const { refresh } = useAuth();
  const [app, setApp] = useState(undefined);
  useEffect(() => {
    api.get("/applications/mine").then((r) => { setApp(r.data.id === appId ? r.data : null); if (r.data.status === "approved") refresh(); }).catch(() => setApp(null));
  }, [appId, refresh]);
  if (app === undefined) return <FullLoader />;
  if (!app) return <Navigate to="/empresas/inscripcion" replace />;
  const step = STEP_OF[app.status];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="company-application-page">
      <SiteHeader />
      <main className="mx-auto max-w-6xl px-5 py-12 lg:px-10 lg:py-16">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-upv">{t("Inscripción de empresas")} · {t("Referencia")} {app.ref}</p>
        <div className="mt-3 flex flex-wrap items-center gap-4">
          <h1 className="font-display text-4xl font-extrabold tracking-tight sm:text-5xl" data-testid="company-application-name">{app.name}</h1>
          <StatusBadge status={app.status} testId="company-application-status" />
        </div>
        <ol className="mt-10 grid gap-3 sm:grid-cols-4" data-testid="company-application-steps">
          {STEPS.map((s, i) => {
            const done = i < step || app.status === "approved";
            return (
              <li key={s} className={`flex items-center gap-3 rounded-2xl border p-4 ${done ? "border-emerald-200 bg-emerald-50" : i === step ? "border-upv bg-white" : "border-slate-200 bg-white"}`}>
                <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${done ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-500"}`}>{done ? <Check className="h-4 w-4" strokeWidth={3} /> : i + 1}</span>
                <span className="text-sm font-semibold">{t(s)}</span>
              </li>
            );
          })}
        </ol>
        <div className="mt-10 grid grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
          <div className="space-y-6">
            {app.status === "pending_payment" ? <TransferCard app={app} onChange={setApp} /> : (
              <div className={`rounded-[28px] p-6 ${app.status === "rejected" ? "bg-rose-50 text-rose-800" : app.status === "approved" ? "bg-emerald-600 text-white" : "bg-white border border-slate-200"}`} data-testid="company-application-message">
                <p className="font-display text-xl font-bold">{t(MESSAGES[app.status])}</p>
                {app.receipt && <p className="mt-2 flex items-center gap-2 text-sm opacity-80"><Paperclip className="h-4 w-4" /> {app.receipt}</p>}
                {app.status === "approved" && <Link to={`/empresas/${app.companySlug}`} className="mt-5 mr-2 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-emerald-700" data-testid="company-application-directory-link">{t("Ver perfil en el directorio")} <ArrowRight className="h-4 w-4" /></Link>}
                {app.status === "approved" && <Link to="/empresa/portal" className="mt-5 inline-flex items-center gap-2 rounded-full bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white" data-testid="company-application-portal-link"><Building2 className="h-4 w-4" /> {t("Abrir portal de empresa")}</Link>}
                {app.status === "approved" && app.stand && <p className="mt-3 text-sm text-white/80" data-testid="company-application-stand">Stand {app.stand}</p>}
              </div>
            )}
          </div>
          <div className="space-y-6">
            <ProfileCard app={app} />
            <UsersCard app={app} />
          </div>
        </div>
      </main>
      <PageFooter />
    </div>
  );
}
